import { serviceCategoriesEnum } from 'components/StrategiesList/SimulationDetailPopUp/DetailTable/helpers';
import {
  IServicesCategories,
  ISimulation,
  ISimulationMailingRule,
  ISimulationResponse,
} from 'components/StrategiesList/SimulationDetailPopUp/DetailTable/interfaces';
import {
  addMilliseconds,
  differenceInMilliseconds,
  formatDistanceToNow,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { diffenceBetweenDates } from 'utils/date';
import {
  MIN_EXECUTED_PERCENT_TO_START,
  simulationConfig,
  TIME_TO_START_PREVISION,
} from './configs';

const calculateTotalOfFields = (
  arrayFields: string[],
  simulation: ISimulation,
) => arrayFields.reduce((total, field) => total + (simulation[field] || 0), 0);

export const getCompletedPercentage = (simulation: ISimulation): number => {
  const simulationConfigItem = simulationConfig[simulation.type];

  if (!simulationConfigItem) {
    console.error(`Invalid simulation type: ${simulation.type}`);
    return 0;
  }

  const { fieldsToCalculate, preparingFields } = simulationConfigItem;

  const totalByFields: number = calculateTotalOfFields(
    fieldsToCalculate,
    simulation,
  );
  const totalToCalculated = calculateTotalOfFields(preparingFields, simulation);

  return totalByFields && totalToCalculated
    ? Number.parseInt(String((totalByFields / totalToCalculated) * 100))
    : 0;
};

interface IPrevisionParams {
  messageTimeRemain: string;
  totalTime: number;
  totalStartExecuted: number;
  lastUpdateDate: string;
}
export const getPrevisionParams = (
  simulation: ISimulation,
): IPrevisionParams => {
  const simulationConfigItem = simulationConfig[simulation.type];

  if (!simulationConfigItem) {
    console.error(`Invalid simulation type: ${simulation.type}`);
    return {
      messageTimeRemain: '',
      totalTime: 0,
      totalStartExecuted: 0,
      lastUpdateDate: String(new Date()),
    };
  }

  const { startAtField, preparingFields, fieldsToCalculate } = simulationConfig[
    simulation.type
  ];
  const simulationStartDate = simulation[startAtField];

  const countPreparing = calculateTotalOfFields(preparingFields, simulation);
  const countExecuted = calculateTotalOfFields(fieldsToCalculate, simulation);

  const executedTotalTime = diffenceBetweenDates(
    new Date(),
    new Date(simulationStartDate),
  );
  const executedPercent = simulation.executedPercent || 0;
  if (
    executedTotalTime < TIME_TO_START_PREVISION &&
    executedPercent < MIN_EXECUTED_PERCENT_TO_START
  ) {
    const messageTimeToStartPrevision = formatDistanceToNow(
      addMilliseconds(new Date(simulationStartDate), TIME_TO_START_PREVISION),
      {
        locale: ptBR,
      },
    );

    return {
      messageTimeRemain: `iniciando cálculo em até ${messageTimeToStartPrevision}`,
      totalTime: 0,
      totalStartExecuted: countExecuted,
      lastUpdateDate: String(new Date()),
    };
  }

  if (!simulation.lastUpdateDate) {
    return {
      messageTimeRemain: '',
      totalTime: 0,
      totalStartExecuted: countExecuted,
      lastUpdateDate: String(new Date()),
    };
  }

  const diffenceBetweenCurrentAndLastTime = differenceInMilliseconds(
    new Date(),
    new Date(simulation.lastUpdateDate),
  );
  const totalTime = simulation.totalTime + diffenceBetweenCurrentAndLastTime;

  const totalToExecute = countPreparing - simulation.totalStartExecuted;
  const totalExecuted = countExecuted - simulation.totalStartExecuted;

  const proccessAvgPerItem = totalExecuted > 0 ? totalTime / totalExecuted : 0;
  const totalToExecuteToFinish = totalToExecute - totalExecuted;
  const previsionFinisihTime = proccessAvgPerItem * totalToExecuteToFinish;

  const parsedPrevisionLabel = proccessAvgPerItem
    ? formatDistanceToNow(addMilliseconds(new Date(), previsionFinisihTime), {
        locale: ptBR,
      })
    : 'calculando...';

  return {
    messageTimeRemain: parsedPrevisionLabel,
    totalTime,
    totalStartExecuted:
      totalToExecuteToFinish > 0 ? simulation.totalStartExecuted : 0,
    lastUpdateDate: String(new Date()),
  };
};

export const isReadySimulationOrDistribution = (status: string): boolean =>
  ['SIMULATION_READY', 'DISTRIBUTION_READY'].includes(status);

export const getSimulationChangeOnlyHeaders = (
  currentSimulation: ISimulation,
  simulationDistribution: ISimulationResponse,
): ISimulation => {
  let simulatingValues = {
    executedPercent: 100,
    messageTimeRemain: '',
    lastUpdateDate: String(new Date()),
    totalTime: 0,
    totalStartExecuted: 0,
  };

  const isReady = isReadySimulationOrDistribution(
    simulationDistribution.status,
  );

  if (!isReady) {
    const parsedSimulationDistribution = (simulationDistribution as unknown) as ISimulation;
    const executedPercent = getCompletedPercentage(
      parsedSimulationDistribution,
    );
    simulatingValues = {
      executedPercent,
      ...getPrevisionParams({
        ...parsedSimulationDistribution,
        executedPercent,
        totalStartExecuted: currentSimulation?.totalStartExecuted,
        totalTime: currentSimulation?.totalTime,
        lastUpdateDate: currentSimulation?.lastUpdateDate,
      }),
    };
  }
  return {
    ...currentSimulation,
    countPreparingSimulation: simulationDistribution.countPreparingSimulation,
    countExecutedSimulation: simulationDistribution.countExecutedSimulation,
    countPreparingDistribution:
      simulationDistribution.countPreparingDistribution,
    countExecutedDistribution: simulationDistribution.countExecutedDistribution,
    status: simulationDistribution.status,
    type: simulationDistribution.type,
    createdAt: simulationDistribution.createdAt,
    updatedAt: simulationDistribution.updatedAt,
    startAtDistribution: simulationDistribution.startAtDistribution,
    startAtSimulation: simulationDistribution.startAtSimulation,
    ...simulatingValues,
    strategy: currentSimulation?.strategy || simulationDistribution?.strategy,
  };
};

export const getParsedSimulation = (
  simulationResponse: ISimulationResponse,
): ISimulation => {
  const serviceCategoriesKeys = Object.keys(serviceCategoriesEnum);
  const simulationCopy = Object.assign({}, simulationResponse);
  const parsedSeviceCategories = serviceCategoriesKeys.reduce(
    (servicesCategories, key) => {
      if (simulationCopy[key]) {
        servicesCategories[key] = (simulationCopy[
          key
        ] as unknown) as ISimulationMailingRule;

        servicesCategories[key] = getSortedServicesCategories(
          servicesCategories[key],
        );

        delete simulationCopy[key];
      }
      return servicesCategories;
    },
    {} as IServicesCategories,
  );

  const parsedSimulation = (simulationCopy as unknown) as ISimulation;

  return {
    ...simulationCopy,
    executedPercent: getCompletedPercentage(parsedSimulation),
    ...getPrevisionParams(parsedSimulation),
    serviceCategories: parsedSeviceCategories,
  };
};

const getSortedServicesCategories = (
  serviceCategory: ISimulationMailingRule,
) => {
  const sortedMailingIds = Object.keys(serviceCategory).sort(
    (first, second) =>
      serviceCategory[first].priority - serviceCategory[second].priority,
  );

  return sortedMailingIds.reduce((serviceCategoryByPriority, id) => {
    serviceCategoryByPriority[id] = serviceCategory[id];
    return serviceCategoryByPriority;
  }, {} as ISimulationMailingRule);
};
